import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, TrendingUp } from 'lucide-react';
import { get } from '@/api/client';
import { queryKeys } from '@/api/queryKeys';
import { cn } from '@/lib/cn';
import { toast } from '@/store/toastStore';

/** 검색은 최소 2글자 이상 입력해야 동작. (자동완성 API 부하 방지 + UX 혼란 방지) */
const MIN_QUERY_LEN = 2;

interface AutocompleteItem {
  kind: 'ticker' | 'user';
  value: string;
  label: string;
  sub: string | null;
}
interface AutocompleteData {
  suggestions: AutocompleteItem[];
}
interface PopularData {
  popular: Array<{ keyword: string; count: number }>;
}

interface SearchBoxProps {
  /** 초기값 (예: SearchPage 에서 URL ?q= 동기화) */
  initialValue?: string;
  /** Enter / 검색 버튼 눌렀을 때 호출. 미지정 시 `/search?q=...` 이동 */
  onSubmit?: (q: string) => void;
  placeholder?: string;
  /** "sm" = 헤더용 h-9, "md" = 페이지용 h-10 */
  size?: 'sm' | 'md';
  /** 검색 버튼 표시 여부 (기본 false — 헤더는 Enter 로만) */
  showButton?: boolean;
  className?: string;
  /** 외부 컨트롤용 — 입력값이 바뀔 때 알림 */
  onValueChange?: (value: string) => void;
  /**
   * 자동완성/인기검색어 렌더 방식
   * - `dropdown` (기본): 입력창 아래 absolute 드롭다운 (PC 헤더 / 페이지 내)
   * - `inline`   : 입력창 아래 block 요소로 그대로 펼쳐짐 (모바일 전체화면 오버레이용)
   */
  variant?: 'dropdown' | 'inline';
}

export function SearchBox({
  initialValue = '',
  onSubmit,
  placeholder = '종목, 게시글을 검색하세요',
  size = 'sm',
  showButton = false,
  className,
  onValueChange,
  variant = 'dropdown',
}: SearchBoxProps) {
  const isInline = variant === 'inline';
  const navigate = useNavigate();
  const [input, setInput] = useState(initialValue);
  const [debounced, setDebounced] = useState(initialValue);
  const [open, setOpen] = useState(false);
  /* 키보드 네비게이션 — 인기 검색어/자동완성 통합 인덱스. -1 = 비활성(입력값 그대로 submit).
     ArrowDown/ArrowUp 으로 이동, Enter 로 선택. 마우스 호버 시에도 동기화. */
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // 외부에서 initialValue 가 바뀌면 동기화 (예: URL ?q= 변경)
  useEffect(() => {
    setInput(initialValue);
    setDebounced(initialValue);
  }, [initialValue]);

  /* 페이지 이동 시 검색어 초기화 — /search 페이지 외 다른 곳으로 가면 비움.
     /search?q=foo 안에서는 SearchPage 가 자체적으로 q 를 다루므로 그대로 유지. */
  const location = useLocation();
  useEffect(() => {
    if (location.pathname !== '/search') {
      setInput('');
      setDebounced('');
    }
  }, [location.pathname]);

  // 300ms debounce
  useEffect(() => {
    const t = setTimeout(() => setDebounced(input.trim()), 300);
    return () => clearTimeout(t);
  }, [input]);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    /* 외부 클릭으로 자동 닫기는 dropdown 변형에서만 필요 — inline 은 항상 펼쳐 있음 */
    if (isInline) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isInline]);

  // 자동완성 — 최소 2글자부터 조회
  const { data: autocomplete } = useQuery({
    queryKey: queryKeys.search.autocomplete(debounced),
    queryFn: () =>
      get<AutocompleteData>(
        `/search/autocomplete?q=${encodeURIComponent(debounced)}`,
      ),
    enabled: open && debounced.length >= MIN_QUERY_LEN,
    staleTime: 60_000,
  });

  // 인기 검색어 (입력이 비어 있을 때만)
  const { data: popular } = useQuery({
    queryKey: queryKeys.search.popular,
    queryFn: () => get<PopularData>('/search/popular'),
    enabled: open && input.trim().length === 0,
    staleTime: 5 * 60_000,
  });

  const suggestions = autocomplete?.suggestions ?? [];
  const popularItems = popular?.popular ?? [];
  /* inline 모드는 항상 펼침 — `open` 게이트 없이 입력 상태에 따라 결정 */
  const resultsGate = isInline ? true : open;
  const showAutocomplete =
    resultsGate && debounced.length >= MIN_QUERY_LEN && suggestions.length > 0;
  const showPopular =
    resultsGate && input.trim().length === 0 && popularItems.length > 0;
  const showMinLenHint =
    resultsGate &&
    input.trim().length > 0 &&
    input.trim().length < MIN_QUERY_LEN;

  /* 키보드 네비게이션 대상 — 현재 펼쳐진 리스트(둘 중 하나만 활성). */
  const navItems: number = showAutocomplete
    ? suggestions.length
    : showPopular
    ? popularItems.length
    : 0;
  /* 입력값/모드 전환 시 activeIndex 리셋 — 사라진 리스트의 인덱스 잔존 방지 */
  useEffect(() => {
    setActiveIndex(-1);
  }, [debounced, showAutocomplete, showPopular]);

  const submit = (keyword: string) => {
    const kw = keyword.trim();
    if (!kw) return;
    if (kw.length < MIN_QUERY_LEN) {
      toast.error(`검색어는 ${MIN_QUERY_LEN}글자 이상 입력해 주세요.`);
      return;
    }
    setInput(kw);
    setOpen(false);
    inputRef.current?.blur();
    if (onSubmit) {
      onSubmit(kw);
    } else {
      navigate(`/search?q=${encodeURIComponent(kw)}`);
    }
  };

  /** 자동완성 항목(종목/유저) 선택 — 상세 페이지로 이동하므로 입력창 값 초기화.
      이전에는 입력 중이던 텍스트("삼")가 그대로 남아 혼란을 유발하던 버그 수정. */
  const selectSuggestion = (item: AutocompleteItem) => {
    setOpen(false);
    inputRef.current?.blur();
    setInput('');
    setDebounced('');
    onValueChange?.('');
    if (item.kind === 'ticker') {
      navigate(`/ticker/${item.value}`);
    } else {
      navigate(`/users/${item.value}`);
    }
  };

  const handleChange = (v: string) => {
    setInput(v);
    onValueChange?.(v);
  };

  const heightClass = size === 'md' ? 'h-10' : 'h-9';

  /* 결과 리스트 본체 — dropdown/inline 공통, variant 에 따라 감싸는 컨테이너만 다름.
   * inline 모드: 각 항목 h-12 이상 (`py-3`) 으로 터치 친화적. */
  const itemPadding = isInline ? 'px-4 py-3' : 'px-3 py-2';
  const resultsBody = (
    <>
      {showPopular && (
        <div>
          <div className="flex items-center gap-1.5 border-b border-[var(--color-border)] px-4 py-2 text-xs font-semibold text-[var(--color-text-sub)]">
            <TrendingUp className="h-3.5 w-3.5" />
            인기 검색어
          </div>
          <ul>
            {popularItems.map((p, idx) => (
              <li key={p.keyword}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    submit(p.keyword);
                  }}
                  onMouseEnter={() => setActiveIndex(idx)}
                  className={cn(
                    'flex w-full items-center gap-3 text-left',
                    activeIndex === idx
                      ? 'bg-[var(--color-primary)]/10 text-[var(--color-primary)]'
                      : 'hover:bg-[var(--color-primary)]/10 hover:text-[var(--color-primary)]',
                    isInline ? 'min-h-12 text-base' : 'text-sm',
                    itemPadding,
                  )}
                >
                  <span className="w-5 shrink-0 text-xs font-semibold text-[var(--color-primary)]">
                    {idx + 1}
                  </span>
                  <span className="truncate">{p.keyword}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showMinLenHint && (
        <div className={cn('text-[var(--color-text-sub)]', itemPadding)}>
          <p className={cn(isInline ? 'text-sm' : 'text-xs')}>
            {MIN_QUERY_LEN}글자 이상 입력해 주세요.
          </p>
        </div>
      )}

      {showAutocomplete && (
        <ul>
          {suggestions.map((item, idx) => (
            <li key={`${item.kind}-${item.value}`}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectSuggestion(item);
                }}
                onMouseEnter={() => setActiveIndex(idx)}
                className={cn(
                  'flex w-full items-center gap-3 text-left',
                  activeIndex === idx
                    ? 'bg-[var(--color-primary)]/10 text-[var(--color-primary)]'
                    : 'hover:bg-[var(--color-primary)]/10 hover:text-[var(--color-primary)]',
                  isInline ? 'min-h-12 text-base' : 'text-sm',
                  itemPadding,
                )}
              >
                {/* 검색 자동완성 — 아이콘/이모지 사용 금지 (종목/자동완성 공통 정책).
                    min-w-0 필수: flex 자식의 truncate 가 동작해 우측 market 뱃지가 짤리지 않도록. */}
                <span className="min-w-0 flex-1 truncate">
                  <span className="font-medium">{item.label}</span>
                  {item.kind === 'ticker' && (
                    <span className="ml-2 text-xs text-[var(--color-text-sub)]">
                      {item.value}
                    </span>
                  )}
                </span>
                {item.sub && (
                  <span className="shrink-0 whitespace-nowrap text-xs text-[var(--color-text-sub)]">
                    {item.sub}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );

  /* 공통 입력창 마크업 */
  const inputEl = (
    <div className="relative flex-1">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-sub)]" />
      <input
        ref={inputRef}
        type="text"
        value={input}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            if (navItems === 0) return;
            e.preventDefault();
            setActiveIndex((i) => (i + 1) % navItems);
          } else if (e.key === 'ArrowUp') {
            if (navItems === 0) return;
            e.preventDefault();
            setActiveIndex((i) => (i <= 0 ? navItems - 1 : i - 1));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            /* 키보드로 선택한 항목이 있으면 그 항목 실행, 아니면 입력값 그대로 submit. */
            if (activeIndex >= 0 && showAutocomplete && suggestions[activeIndex]) {
              selectSuggestion(suggestions[activeIndex]);
            } else if (activeIndex >= 0 && showPopular && popularItems[activeIndex]) {
              submit(popularItems[activeIndex].keyword);
            } else {
              submit(input);
            }
          } else if (e.key === 'Escape') {
            setOpen(false);
            inputRef.current?.blur();
          }
        }}
        placeholder={placeholder}
        className={cn(
          'w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] pl-9 pr-3 text-sm',
          'placeholder:text-[var(--color-text-sub)]',
          'focus:border-[var(--color-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30',
          heightClass,
        )}
      />

      {/* dropdown 변형 — 입력창 아래 absolute 팝업 */}
      {!isInline && (showAutocomplete || showPopular || showMinLenHint) && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)]">
          {resultsBody}
        </div>
      )}
    </div>
  );

  /* inline 변형 — 수직 스택: 입력창 + 결과 블록 (오버레이 전체 높이 채움) */
  if (isInline) {
    return (
      <div
        ref={containerRef}
        className={cn('flex h-full flex-col', className)}
      >
        <div className="flex items-center gap-2">
          {inputEl}
          {showButton && (
            <button
              type="button"
              onClick={() => submit(input)}
              className={cn(
                'rounded-lg bg-[var(--color-primary)] px-4 text-sm font-semibold text-white',
                heightClass,
              )}
            >
              검색
            </button>
          )}
        </div>
        {(showAutocomplete || showPopular || showMinLenHint) && (
          <div className="mt-2 flex-1 overflow-y-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)]">
            {resultsBody}
          </div>
        )}
      </div>
    );
  }

  /* dropdown 변형 (기본) — 수평 스택: 입력창 + (선택) 검색 버튼 */
  return (
    <div ref={containerRef} className={cn('relative flex items-center gap-2', className)}>
      {inputEl}
      {showButton && (
        <button
          type="button"
          onClick={() => submit(input)}
          className={cn(
            'rounded-lg bg-[var(--color-primary)] px-4 text-sm font-semibold text-white',
            heightClass,
          )}
        >
          검색
        </button>
      )}
    </div>
  );
}
